import { useMemo } from 'react'

import type { WorkflowAction } from './useIncidentWorkflow'

import { useUsersQuery } from '@/graphql/generated/hooks'
import { abbreviateName } from '@/utils/formats'

export const formatActionTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp)
  if (isNaN(date.getTime())) return timestamp
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

export const useWorkflowActionUserNames = (
  workflowActions: WorkflowAction[]
): Map<string, string> => {
  const userIds = useMemo(
    () => [...new Set(workflowActions.map((a) => a.userId))].sort(),
    [workflowActions]
  )

  const { data } = useUsersQuery({
    fetchPolicy: 'cache-and-network',
    skip: userIds.length === 0,
    variables: { filter: { idIn: userIds } },
  })

  return useMemo(() => {
    const map = new Map<string, string>()
    for (const edge of data?.users?.edges ?? []) {
      if (!edge?.node) continue
      const { id, firstName, lastName } = edge.node
      const fullName = [firstName, lastName].filter(Boolean).join(' ')
      map.set(id, fullName ? abbreviateName(fullName) : '')
    }
    return map
  }, [data])
}
